// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

/* eslint-disable react-refresh/only-export-components -- Context modules export both Provider and hook by design */
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '../i18n';
import api from '../api/client';
import { useAuth } from '../auth/AuthProvider';

// Raw API response shape
interface TerminologyResponse {
  event_singular: string;
  event_plural: string;
  registration_singular: string;
  registration_plural: string;
  participant_singular: string;
  participant_plural: string;
  instructor_singular: string;
  instructor_plural: string;
  venue_singular: string;
  venue_plural: string;
  membership_singular: string;
  membership_plural: string;
  payment_singular: string;
  payment_plural: string;
  dashboard_singular: string;
  dashboard_plural: string;
  navigation_singular: string;
  navigation_plural: string;
  schedule_singular: string;
  schedule_plural: string;
  announcement_singular: string;
  announcement_plural: string;
}

// User-friendly terminology interface with short names
export interface Terminology {
  event: string;
  events: string;
  registration: string;
  registrations: string;
  participant: string;
  participants: string;
  instructor: string;
  instructors: string;
  venue: string;
  venues: string;
  membership: string;
  memberships: string;
  payment: string;
  payments: string;
  dashboard: string;
  dashboards: string;
  navigation: string;
  navigations: string;
  schedule: string;
  schedules: string;
  announcement: string;
  announcements: string;
  loading: boolean;
}

// Default terminology (general club type)
const defaultTerminology: Terminology = {
  event: 'Event',
  events: 'Events',
  registration: 'Registration',
  registrations: 'Registrations',
  participant: 'Participant',
  participants: 'Participants',
  instructor: 'Organizer',
  instructors: 'Organizers',
  venue: 'Venue',
  venues: 'Venues',
  membership: 'Membership',
  memberships: 'Memberships',
  payment: 'Payment',
  payments: 'Payments',
  dashboard: 'Dashboard',
  dashboards: 'Dashboards',
  navigation: 'Menu',
  navigations: 'Menu',
  schedule: 'Schedule',
  schedules: 'Schedules',
  announcement: 'Announcement',
  announcements: 'Announcements',
  loading: false,
};

// Inject terminology values into i18next's 'terminology' namespace so
// components can use t('terminology:event') alongside useTerminology().
function syncToI18n(t: Omit<Terminology, 'loading'>) {
  const bundle: Record<string, string> = {};
  for (const [key, val] of Object.entries(t)) {
    if (typeof val === 'string') bundle[key] = val;
  }
  i18n.addResourceBundle('en', 'terminology', bundle, true, true);
}

const TerminologyContext = createContext<Terminology | undefined>(undefined);

export function TerminologyProvider({ children }: { children: ReactNode }) {
  const { isAuthenticating, isAuthenticated } = useAuth();
  const [terminology, setTerminology] = useState<Terminology>({ ...defaultTerminology, loading: true });

  useEffect(() => {
    // Don't fetch until auth is complete
    if (isAuthenticating) {
      return;
    }

    // Only fetch if authenticated
    if (!isAuthenticated) {
      syncToI18n(defaultTerminology);
      setTerminology({ ...defaultTerminology, loading: false });
      return;
    }

    const fetchTerminology = async () => {
      try {
        const response = await api.get<TerminologyResponse>('/clubs/me/terminology');
        const data = response.data;
        const fetched: Terminology = {
          event: data.event_singular,
          events: data.event_plural,
          registration: data.registration_singular,
          registrations: data.registration_plural,
          participant: data.participant_singular,
          participants: data.participant_plural,
          instructor: data.instructor_singular,
          instructors: data.instructor_plural,
          venue: data.venue_singular,
          venues: data.venue_plural,
          membership: data.membership_singular,
          memberships: data.membership_plural,
          payment: data.payment_singular,
          payments: data.payment_plural,
          dashboard: data.dashboard_singular,
          dashboards: data.dashboard_plural,
          navigation: data.navigation_singular,
          navigations: data.navigation_plural,
          schedule: data.schedule_singular,
          schedules: data.schedule_plural,
          announcement: data.announcement_singular,
          announcements: data.announcement_plural,
          loading: false,
        };
        syncToI18n(fetched);
        setTerminology(fetched);
      } catch {
        // Fall back to default terminology on error
        syncToI18n(defaultTerminology);
        setTerminology({ ...defaultTerminology, loading: false });
      }
    };

    fetchTerminology();
  }, [isAuthenticating, isAuthenticated]);

  const value = useMemo(() => terminology, [terminology]);

  return <TerminologyContext.Provider value={value}>{children}</TerminologyContext.Provider>;
}

export function useTerminology(): Terminology {
  const ctx = useContext(TerminologyContext);
  if (!ctx) {
    throw new Error('useTerminology must be used within a TerminologyProvider');
  }
  return ctx;
}

// Export default terminology for use in tests and fallbacks
export { defaultTerminology };
